import { LogLevel } from '@unistocks/types';
import { channel } from 'expo-updates';
import { Alert } from 'react-native';
import { isWeb, type ThemeName } from 'tamagui';

export interface Environment {
  production: boolean;
  isJest: boolean;
  logLevel: LogLevel;
  apiUrl: string;
  universalLinkBaseUrl: string;
  appName: string;
  theme: ThemeName;
  oAuthConfig: {
    issuer: string;
    clientId: string;
    scope: string;
  };
  appLovin: {
    sdkKey: string;
    adReviewKey: string;
    android: {
      bannerAdUnitId: string;
      nativeAdUnitId: string;
    };
    ios: {
      bannerAdUnitId: string;
      nativeAdUnitId: string;
    };
  };
  sentry: {
    enable: boolean;
    dsn: string;
    environment: string;
  };
}

type Envrionments = {
  development: Environment;
  staging: Environment;
  production: Environment;
};

const environment = channel || process.env.EXPO_PUBLIC_ENVIRONMENT;
const apiUrl = isWeb
  ? process.env.EXPO_PUBLIC_API_URL!
  : process.env.EXPO_PUBLIC_NATIVE_API_URL! || process.env.EXPO_PUBLIC_API_URL!;
const theme: ThemeName = 'blue';
const appName = 'React-Native';
const sentryDsn = process.env.EXPO_PUBLIC_SENTRY_DSN!;

const Channel = {
  Development: 'development',
  Staging: 'staging',
  Beta: 'beta',
  Production: 'production'
} as const;

const ENV: Envrionments = {
  development: {
    production: false,
    isJest: !!process.env.JEST_WORKER_ID,
    logLevel: 'debug',
    apiUrl,
    universalLinkBaseUrl: 'https://unistocks-dev.ngrok.io',
    appName,
    oAuthConfig: {
      issuer: apiUrl,
      clientId: 'Unistocks_App',
      scope: 'offline_access Unistocks'
    },
    theme,
    appLovin: {
      sdkKey: '',
      adReviewKey: '',
      android: {
        bannerAdUnitId: '',
        nativeAdUnitId: ''
      },
      ios: {
        bannerAdUnitId: '',
        nativeAdUnitId: ''
      }
    },
    sentry: {
      enable: false,
      dsn: sentryDsn,
      environment: 'Development'
    }
  },
  staging: {
    production: true,
    isJest: false,
    logLevel: 'warn',
    apiUrl,
    universalLinkBaseUrl: 'https://app.staging.unistocks.se',
    appName,
    oAuthConfig: {
      issuer: apiUrl,
      clientId: 'Unistocks_App',
      scope: 'offline_access Unistocks'
    },
    theme,
    appLovin: {
      sdkKey: '',
      adReviewKey: '',
      android: {
        bannerAdUnitId: '',
        nativeAdUnitId: ''
      },
      ios: {
        bannerAdUnitId: '',
        nativeAdUnitId: ''
      }
    },
    sentry: {
      enable: true,
      dsn: sentryDsn,
      environment: 'Staging'
    }
  },
  production: {
    production: true,
    isJest: false,
    logLevel: 'error',
    apiUrl,
    universalLinkBaseUrl: 'https://app.unistocks.se',
    appName,
    oAuthConfig: {
      issuer: apiUrl,
      clientId: 'Unistocks_App',
      scope: 'offline_access Unistocks'
    },
    theme,
    appLovin: {
      sdkKey: '',
      adReviewKey: '',
      android: {
        bannerAdUnitId: '',
        nativeAdUnitId: ''
      },
      ios: {
        bannerAdUnitId: '',
        nativeAdUnitId: ''
      }
    },
    sentry: {
      enable: true,
      dsn: sentryDsn,
      environment: 'Production'
    }
  }
};

export const getEnvVars = () => {
  if (__DEV__) {
    return ENV.development;
  }

  switch (environment) {
    case Channel.Development:
      return ENV.development;

    case Channel.Staging:
      return ENV.staging;

    case Channel.Beta:
    case Channel.Production:
      return ENV.production;
  }

  Alert.prompt(
    `No environment variables found for this build type! (${environment})', 'Please check your build configuration.`
  );

  return ENV.production;
};
